/**
 * LinkBookingToDealDialog - Dialog para vincular booking standalone a um negócio
 * Permite buscar deals existentes e associar ao booking selecionado
 * 
 * @version 2.0.0
 * RELEASE: 2026-02-17 v1.0.0 - Implementação inicial
 * RELEASE: 2026-02-20 v2.0.0 - Redesign visual: cards com badges, ScrollArea fixa, contador, estados vazios
 */
import React, { useState, useCallback } from 'react';
import { Search, Link2, Loader2, User, Phone, Mail, SearchX } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

/** Props do componente */
interface LinkBookingToDealDialogProps {
  /** Se o dialog está aberto */
  open: boolean;
  /** Callback para abrir/fechar */
  onOpenChange: (open: boolean) => void;
  /** ID do booking a vincular */
  bookingId: string | null;
  /** Nome do convidado (para exibição) */
  guestName?: string;
  /** Callback ao vincular com sucesso */
  onLinked: (bookingId: string, dealId: string) => void;
  /** Se a mutation de vincular está pendente */
  isLinking?: boolean;
}

/** Deal retornado pela busca */
interface DealSearchResult {
  id: string;
  title: string;
  pipeline_name: string | null;
  phase_name: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
}

/**
 * Dialog para vincular booking standalone a um deal existente
 */
export function LinkBookingToDealDialog({
  open,
  onOpenChange,
  bookingId,
  guestName,
  onLinked,
  isLinking,
}: LinkBookingToDealDialogProps) {
  const { currentTenant } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<DealSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  /** Flag que indica se a busca já foi executada ao menos uma vez */
  const [hasSearched, setHasSearched] = useState(false);

  /**
   * Busca deals pelo título ou nome do cliente
   */
  const handleSearch = useCallback(async () => {
    if (!currentTenant?.id || searchTerm.trim().length < 2) return;

    setIsSearching(true);
    setHasSearched(true);
    try {
      // Chamada RPC com busca via SQL puro (resolve limitação do PostgREST)
      const { data, error } = await supabase.rpc('search_deals_for_linking', {
        p_search_term: searchTerm.trim(),
        p_tenant_hint: currentTenant.id,
      });

      if (error) throw error;

      const mapped: DealSearchResult[] = (data || []).map((d: any) => ({
        id: d.id,
        title: d.title,
        pipeline_name: d.pipeline_name,
        phase_name: d.phase_name,
        customer_name: d.customer_name,
        customer_phone: d.customer_phone,
        customer_email: d.customer_email,
      }));

      setResults(mapped);
    } catch (err: any) {
      console.error('[LinkBookingToDeal] Erro RPC:', err);
      toast.error('Erro ao buscar negócios');
    } finally {
      setIsSearching(false);
    }
  }, [currentTenant?.id, searchTerm]);

  /** Handler para tecla Enter na busca */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  /** Handler para confirmar vinculação */
  const handleConfirm = () => {
    if (!bookingId || !selectedDealId) return;
    onLinked(bookingId, selectedDealId);
  };

  /** Limpa estado ao fechar */
  const handleOpenChange = (v: boolean) => {
    if (!v) {
      setSearchTerm('');
      setResults([]);
      setSelectedDealId(null);
      setHasSearched(false);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            Vincular a Negócio
          </DialogTitle>
          <DialogDescription>
            {guestName
              ? `Vincule o agendamento de "${guestName}" a um negócio existente.`
              : 'Vincule este agendamento a um negócio existente.'}
          </DialogDescription>
        </DialogHeader>

        {/* Campo de busca */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por título, nome, telefone ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-9"
              autoFocus
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSearch}
            disabled={isSearching || searchTerm.trim().length < 2}
            className="gap-1.5"
          >
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Search className="h-3.5 w-3.5" />
                Buscar
              </>
            )}
          </Button>
        </div>

        {/* Contador de resultados */}
        {hasSearched && !isSearching && results.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {results.length} {results.length === 1 ? 'negócio encontrado' : 'negócios encontrados'}
          </p>
        )}

        {/* Resultados com ScrollArea fixa */}
        <ScrollArea className="h-[340px]" axis="y" reserveScrollbarSpace>
          {/* Estado inicial - antes de buscar */}
          {!hasSearched && !isSearching && (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
              <Search className="h-10 w-10 opacity-30" />
              <p className="text-sm">Digite ao menos 2 caracteres e clique em Buscar</p>
            </div>
          )}

          {/* Estado vazio - buscou mas não encontrou */}
          {results.length === 0 && !isSearching && hasSearched && (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
              <SearchX className="h-10 w-10 opacity-30" />
              <p className="text-sm">Nenhum negócio encontrado</p>
              <p className="text-xs opacity-70">Tente outro termo de busca</p>
            </div>
          )}

          {/* Loading */}
          {isSearching && (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Cards de resultado */}
          <div className="space-y-2">
            {results.map((deal) => (
              <button
                key={deal.id}
                className={cn(
                  'w-full text-left p-3 rounded-lg border transition-all',
                  selectedDealId === deal.id
                    ? 'border-primary bg-primary/5 border-l-4 border-l-primary shadow-sm'
                    : 'border-border hover:bg-muted/50 hover:shadow-sm'
                )}
                onClick={() => setSelectedDealId(deal.id)}
              >
                {/* Título do deal */}
                <p className="text-sm font-semibold text-foreground truncate">{deal.title}</p>

                {/* Badges de pipeline e fase */}
                {(deal.pipeline_name || deal.phase_name) && (
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {deal.pipeline_name && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
                        {deal.pipeline_name}
                      </Badge>
                    )}
                    {deal.phase_name && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">
                        {deal.phase_name}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Informações do cliente */}
                {(deal.customer_name || deal.customer_phone || deal.customer_email) && (
                  <div className="mt-2 space-y-0.5">
                    {deal.customer_name && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <User className="h-3 w-3 shrink-0" />
                        <span className="truncate">{deal.customer_name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 flex-wrap">
                      {deal.customer_phone && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Phone className="h-3 w-3 shrink-0" />
                          <span>{deal.customer_phone}</span>
                        </div>
                      )}
                      {deal.customer_email && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3 shrink-0" />
                          <span className="truncate max-w-[180px]">{deal.customer_email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        </ScrollArea>

        {/* Botão confirmar */}
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            size="sm"
            disabled={!selectedDealId || isLinking}
            onClick={handleConfirm}
          >
            {isLinking ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                Vinculando...
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4 mr-1" />
                Vincular
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
